import type { DriveOmfzFile } from './driveApi';
import { driveHistoryTitle } from './driveHistoryTitle';
import { deriveDisplayTitle } from './fileNaming';
import { PART_LABELS } from '../constants/fingering';
import type { FingeringMode, FingeringPart } from '../types/fingering';

export interface DriveHistoryMeta {
  fullName: string;
  modeLabel: '総譜' | 'パート譜' | null;
  partLabel: string | null;
}

const MODE_LABEL: Record<FingeringMode, '総譜' | 'パート譜'> = { score: '総譜', part: 'パート譜' };

/**
 * issue #169: Drive 履歴 1 件を「表示メタ」へ導出する純関数。
 * 情報源の正 = 保存時 appProperties。欠落時は推測せず driveHistoryTitle (description→name) へ
 * フォールバックし、mode/part 由来の項目 (バッジ・パート表記) は null にして非表示にする。
 * partLabel は総譜 (score) のときだけ PART_LABELS で 1st〜4th に変換する (未知 part は生値)。
 * issue #173: title が存在するときは deriveDisplayTitle で素の曲名へ正規化する (旧データの
 * 汚染 title も表示時にクリーン化)。title 欠落時の #169 フォールバック挙動は不変。
 */
export function deriveHistoryMeta(file: DriveOmfzFile): DriveHistoryMeta {
  const ap = file.appProperties;
  const fullName = ap?.title != null ? deriveDisplayTitle(ap.title) : driveHistoryTitle(file);
  const modeLabel = ap?.mode === 'score' || ap?.mode === 'part' ? MODE_LABEL[ap.mode] : null;
  const partLabel =
    ap?.mode === 'score' && ap.part ? (PART_LABELS[ap.part as FingeringPart] ?? ap.part) : null;
  return { fullName, modeLabel, partLabel };
}

/**
 * 文字列の UTF-8 バイト長を数える。TextEncoder に依存せず全環境 (ブラウザ / jsdom) で動く。
 * サロゲートペア (絵文字等) は 4 バイトとして 1 度に数える。
 */
function utf8ByteLength(str: string): number {
  let bytes = 0;
  for (let i = 0; i < str.length; i += 1) {
    const code = str.charCodeAt(i);
    if (code < 0x80) bytes += 1;
    else if (code < 0x800) bytes += 2;
    else if (code >= 0xd800 && code <= 0xdbff) {
      bytes += 4;
      i += 1; // 下位サロゲートを飛ばす
    } else bytes += 3;
  }
  return bytes;
}

/**
 * issue #169: 保存時に Drive ファイルへ書き添える appProperties を組み立てる。
 * Drive の制約: appProperties 1 エントリは key+value ≤ 124 bytes(UTF-8)。title のみ超過し得るので
 * graceful degrade する (超過時は title を落とし、mode/part だけは必ず残して保存自体は成功させる)。
 * issue #173: 格納前に deriveDisplayTitle で素の曲名へ正規化する (モード/パートのサフィックスは
 * バッジで別表示するため title には残さない)。byte 長判定は正規化後の値で行う。
 */
export function buildHistoryAppProperties(
  title: string,
  mode: FingeringMode,
  part: string,
  pdfFileId?: string,
): { title?: string; mode: string; part: string; pdfFileId?: string } {
  const cleanTitle = deriveDisplayTitle(title);
  const base: { mode: string; part: string; pdfFileId?: string } = { mode, part };
  // issue #177: fileId は短い (~33 字) ため byte 予算に影響せず常に載せる (title 超過時も残す)。
  if (pdfFileId) base.pdfFileId = pdfFileId;
  return utf8ByteLength(cleanTitle) + 'title'.length <= 124 ? { title: cleanTitle, ...base } : base;
}
